import React from 'react'

import { Button, Icon, ShoppingBasketAdd } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { number, func, bool, oneOf } from 'prop-types'

import useGA4ItemTracking from 'Pods/ItemPage/Hooks/useGA4ItemTracking'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useSentry from 'Pods/Shared/Hooks/useSentry'

function AddToCartButton({ productId, onSuccess = null, disabled = false, variant = 'primary' }) {
  const isSecondary = variant === 'secondary'
  const buttonType = isSecondary ? 'secondary' : 'primary-warning'
  const buttonClass = isSecondary
    ? 'pricing-area__actions-button pricing-area__actions-left-button'
    : 'pricing-area__actions-button pricing-area__actions-right-button'

  const { captureException } = useSentry()
  const { postData, isLoading: isPostDataLoading } = useApiPost()
  const { trackAddToCart } = useGA4ItemTracking()

  const handleAddToCartClick = () => {
    const payload = {
      search_query: window?.pageConfig?.product?.searchQuery,
      index: window?.pageConfig?.indexTrack,
    }

    postData({
      path: `/api/internal/cg/items/${productId}/cart_items`,
      data: payload,
      onSuccess: (addToCartResponse) => {
        const { data: { meta: cartData } } = addToCartResponse

        const {
          minPurchaseAmountForCld: applicableCldValue,
          storeCreditsLeft: creditsAvailable,
          saleCampaignActive: isSaleOff,
          itemIds,
          saleOffColor,
          saleOffDiscount,
          saleOffTitle,
          size,
          finalPrice: value,
        } = cartData

        const updatePageConfigCartData = () => {
          window.pageConfig.cart = {
            ...window.pageConfig.cart,
            applicableCldValue,
            creditsAvailable,
            isSaleOff,
            itemIds,
            saleOffColor,
            saleOffDiscount,
            saleOffTitle,
            size,
            value,
          }

          const cartItem = (cartData.trackingCartItems || []).find(item => String(item.id) === productId)
          if (cartItem) {
            window.pageConfig.cart_items.push(cartItem)
          }
        }

        updatePageConfigCartData()

        trackAddToCart()

        // The nav cart opens immediately after add-to-cart, so seed it with this payload
        // before the follow-up refresh finishes and React 18 renders stale popover state.
        App.events.publish('app:cart:added', addToCartResponse.data)
        App.events.publish('top:menu:cart:open')

        onSuccess?.()
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.errors?.detail || 'Failed to add item to cart'
        window.showNotification(errorMessage, {
          type: 'error',
          autohide: true,
        })
        captureException(error)
      },
    })
  }

  return (
    <Button
      type={buttonType}
      customClass={buttonClass}
      size="large"
      trailIcon={<Icon svgComponent={ShoppingBasketAdd} />}
      onClick={handleAddToCartClick}
      disabled={disabled || isPostDataLoading}
      aria-label="Add to cart"
    >
      Add to cart
    </Button>
  )
}

AddToCartButton.propTypes = {
  productId: number.isRequired,
  onSuccess: func,
  disabled: bool,
  variant: oneOf(['primary', 'secondary']),
}

export default AddToCartButton
